export type PrincipalType = 'user' | 'engine' | 'app' | 'service';

export type Principal = {
  id: string;
  type: PrincipalType;
  tenantId?: string;
  email?: string;
  roles: string[];
  raw?: Record<string, unknown>;
};

export type PrincipalClaimMap = {
  principalId?: string;
  principalType?: string;
  tenantId?: string;
  email?: string;
  roles?: string;
};

export type TokenSource =
  | { type: 'authorization-header' }
  | { type: 'cookie'; cookieName: string }
  | { type: 'both'; cookieName: string };

export type SessionStorageAdapter = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export type AuthFailureLike = {
  status?: number;
  statusCode?: number;
  code?: string | null;
  message?: string | null;
  error?: {
    code?: string | null;
    message?: string | null;
  } | null;
};

export type ExpiredJwtMatcherOptions = {
  expiredCodes?: readonly string[];
  requireUnauthorizedStatus?: boolean;
};

export type RefreshRetryOptions<TToken, TResult> = {
  token: TToken;
  request: (token: TToken) => Promise<TResult>;
  refreshToken: (expiredToken: TToken) => Promise<TToken | null | undefined>;
  shouldRefresh?: (error: unknown) => boolean;
};

const DEFAULT_CLAIMS: Required<PrincipalClaimMap> = {
  principalId: 'principal_id',
  principalType: 'principal_type',
  tenantId: 'tenant_id',
  email: 'email',
  roles: 'roles',
};

const DEFAULT_EXPIRED_CODES = [
  'token_expired',
  'jwt_expired',
  'access_token_expired',
] as const;

export function parseAuthorizationBearer(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const match = value.match(/^Bearer\s+(.+)$/i);
  const token = match?.[1]?.trim();
  return token || null;
}

export function parseCookieHeader(header: string | null | undefined): Record<string, string> {
  if (!header) {
    return {};
  }

  return Object.fromEntries(
    header
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const separatorIndex = part.indexOf('=');
        if (separatorIndex === -1) {
          return [part, ''] as const;
        }

        const key = part.slice(0, separatorIndex).trim();
        const value = part.slice(separatorIndex + 1).trim();
        return [key, decodeURIComponent(value)] as const;
      }),
  );
}

export function extractToken(input: {
  authorization?: string | null;
  cookie?: string | null;
  source?: TokenSource;
}): string | null {
  const source = input.source ?? { type: 'authorization-header' };

  if (source.type === 'authorization-header' || source.type === 'both') {
    const headerToken = parseAuthorizationBearer(input.authorization);
    if (headerToken) {
      return headerToken;
    }
  }

  if (source.type === 'cookie' || source.type === 'both') {
    return parseCookieHeader(input.cookie)[source.cookieName] || null;
  }

  return null;
}

export function normalizePrincipalType(value: unknown): PrincipalType | undefined {
  if (value === 'user' || value === 'engine' || value === 'app' || value === 'service') {
    return value;
  }

  return undefined;
}

export function stringClaim(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function stringArrayClaim(payload: Record<string, unknown>, key: string): string[] {
  const value = payload[key];
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/[,\s]+/u)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
}

export function principalFromClaims(
  payload: Record<string, unknown>,
  claimMap: PrincipalClaimMap = {},
): Principal | null {
  const claims = { ...DEFAULT_CLAIMS, ...claimMap };
  const id = stringClaim(payload, claims.principalId) ?? stringClaim(payload, 'sub');
  const type = normalizePrincipalType(
    stringClaim(payload, claims.principalType) ?? stringClaim(payload, 'type') ?? 'user',
  );

  if (!id || !type) {
    return null;
  }

  return {
    id,
    type,
    tenantId: stringClaim(payload, claims.tenantId),
    email: stringClaim(payload, claims.email),
    roles: stringArrayClaim(payload, claims.roles),
    raw: payload,
  };
}

export function createOidcJwksUrl(issuerUrl: string, explicitJwksUrl?: string): string {
  if (explicitJwksUrl?.trim()) {
    return explicitJwksUrl.trim();
  }

  return `${issuerUrl.replace(/\/+$/u, '')}/protocol/openid-connect/certs`;
}

export function saveSessionValue(
  storage: SessionStorageAdapter | undefined,
  key: string,
  value: string | null | undefined,
) {
  if (!storage) {
    return;
  }

  if (!value || value === 'undefined' || value === 'null') {
    storage.removeItem(key);
    return;
  }

  storage.setItem(key, value);
}

export function readSessionValue(storage: SessionStorageAdapter | undefined, key: string): string | null {
  if (!storage) {
    return null;
  }

  const value = storage.getItem(key);
  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }

  return value;
}

function authFailureField(
  failure: AuthFailureLike,
  field: 'code' | 'message',
): string {
  const nested = failure.error?.[field];
  const direct = failure[field];
  const value = nested ?? direct;
  return typeof value === 'string' ? value : '';
}

export function isExpiredJwtAuthFailure(
  failure: AuthFailureLike,
  options: ExpiredJwtMatcherOptions = {},
): boolean {
  const requireUnauthorizedStatus = options.requireUnauthorizedStatus ?? true;
  const statusCode = failure.statusCode ?? failure.status;
  if (requireUnauthorizedStatus && statusCode !== 401) {
    return false;
  }

  const code = authFailureField(failure, 'code').toLowerCase();
  const message = authFailureField(failure, 'message').toLowerCase();
  const expiredCodes = options.expiredCodes ?? DEFAULT_EXPIRED_CODES;

  return (
    expiredCodes.some((expiredCode) => code === expiredCode.toLowerCase()) ||
    (message.includes('jwt') && message.includes('expired')) ||
    (message.includes('access token') && message.includes('expired'))
  );
}

export async function retryOnExpiredJwt<TToken, TResult>({
  token,
  request,
  refreshToken,
  shouldRefresh = (error) =>
    typeof error === 'object' &&
    error !== null &&
    isExpiredJwtAuthFailure(error as AuthFailureLike),
}: RefreshRetryOptions<TToken, TResult>): Promise<TResult> {
  try {
    return await request(token);
  } catch (error) {
    if (!shouldRefresh(error)) {
      throw error;
    }

    const refreshedToken = await refreshToken(token);
    if (!refreshedToken) {
      throw error;
    }

    return request(refreshedToken);
  }
}

export function createTokenRefreshCoordinator<TInput, TResult>(
  refreshToken: (expiredToken?: TInput) => Promise<TResult>,
) {
  let refreshPromise: Promise<TResult> | null = null;

  return async (expiredToken?: TInput) => {
    if (!refreshPromise) {
      refreshPromise = refreshToken(expiredToken).finally(() => {
        refreshPromise = null;
      });
    }

    return refreshPromise;
  };
}
